import { useQuery, useMutation } from '@tanstack/react-query';
import { regulatoryReadinessClient } from '../api/client';

const HEAT_MAP_KEY = 'regulatoryHeatMap';
const JURISDICTION_KEY = 'jurisdictionProfile';
const ENTITY_CONFIGS_KEY = 'entityConfigs';

/**
 * Fetch heat map data for an entity configuration.
 *
 * Calls GET /api/v1/regulatory/heat-map which is proxied to
 * /api/admin/regulatory-readiness/heat-map on platform-admin-api (port 9302).
 */
export function useHeatMap(entityConfigId: string | undefined) {
  return useQuery({
    queryKey: [HEAT_MAP_KEY, entityConfigId],
    queryFn: async () => {
      if (!entityConfigId) return null;
      const { data, error } = await regulatoryReadinessClient.GET('/api/v1/regulatory/heat-map', {
        params: { query: { entityConfigId } },
      });
      if (error) throw new Error((error as { message?: string }).message ?? 'Failed to load heat map');
      return data;
    },
    enabled: !!entityConfigId,
  });
}

/**
 * Fetch jurisdiction profile by two-letter code.
 */
export function useJurisdictionProfile(jurisdictionCode: string | undefined) {
  return useQuery({
    queryKey: [JURISDICTION_KEY, jurisdictionCode],
    queryFn: async () => {
      if (!jurisdictionCode) return null;
      const { data, error } = await regulatoryReadinessClient.GET(
        '/api/v1/regulatory/jurisdictions/{jurisdictionCode}',
        { params: { path: { jurisdictionCode } } }
      );
      if (error)
        throw new Error(
          (error as { message?: string }).message ?? 'Failed to load jurisdiction profile'
        );
      return data;
    },
    enabled: !!jurisdictionCode,
  });
}

/**
 * Search and list entity configurations for the FI selector.
 */
export function useEntityConfigs(searchQuery: string) {
  return useQuery({
    queryKey: [ENTITY_CONFIGS_KEY, searchQuery],
    queryFn: async () => {
      const params: { query: { q?: string } } = { query: {} };
      if (searchQuery) {
        params.query.q = searchQuery;
      }
      const { data, error } = await regulatoryReadinessClient.GET(
        '/api/v1/regulatory/entity-configs',
        { params }
      );
      if (error)
        throw new Error(
          (error as { message?: string }).message ?? 'Failed to fetch entity configs'
        );
      return data;
    },
  });
}

/**
 * Download a PDF report (executive summary or readiness analysis).
 *
 * Uses openapi-fetch with parseAs: 'blob' to retrieve the binary PDF
 * response. The spec declares `"application/pdf": string` for the 200
 * response; at runtime parseAs overrides parsing and yields a Blob, so
 * the cast to `unknown as Blob` is correct.
 */
export function useDownloadReport() {
  return useMutation({
    mutationFn: async ({
      type,
      entityConfigId,
      jurisdictions,
    }: {
      type: 'executive-summary' | 'readiness-analysis';
      entityConfigId: string;
      jurisdictions?: string[];
    }) => {
      const isSummary = type === 'executive-summary';

      // The two report endpoints have distinct required body shapes (spec-driven).
      // All boolean flags default to true per @default annotations in the spec.
      const { data, error } = isSummary
        ? await regulatoryReadinessClient.POST(
            '/api/v1/regulatory/reports/executive-summary',
            {
              body: {
                entityConfigId,
                jurisdictions,
                includeInstitutionalProfile: true,
                includeActionSummary: true,
              },
              parseAs: 'blob',
            }
          )
        : await regulatoryReadinessClient.POST(
            '/api/v1/regulatory/reports/readiness-analysis',
            {
              body: {
                entityConfigId,
                jurisdictions,
                includeConsumerProtection: true,
                includePrivacyOverlay: true,
                includeTrustCompany: true,
                includeCrossJurisdiction: true,
                includeChecklist: true,
              },
              parseAs: 'blob',
            }
          );

      if (error) {
        const msg = (error as { message?: string }).message ?? 'Report generation failed';
        throw new Error(msg);
      }

      // parseAs: 'blob' yields a Blob at runtime; spec types it as string.
      const blob = data as unknown as Blob;
      const downloadUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = isSummary
        ? 'regulatory-readiness-executive-summary.pdf'
        : 'regulatory-readiness-analysis.pdf';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(downloadUrl);
    },
  });
}
